import React from "react";
import "./Reports.css";
// Import UI components for styling and tooltips
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Info } from "lucide-react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";

import Pagination from "../../components/Pagination/Pagination";
import {
  formatCurrency,
  formatDateTime,
  // formatDate,
  mapStatusToBootstrap,
} from "../../components/Constants/Constants";

import AppLoader from "../../components/AppLoader/appLoader";
// import UnAuthorized from "../UnAuthorized/UnAuthorized";
// import usePermission from "../../utils/usePermission";
import { useReports } from "../../customHooks/Reports/useReports";
import CopyIcon from "../../components/CopyIcon/CopyIcon";

// Define column information for Report Tooltips
const reportColumnInfo = {
  refTxnId: "Reference Transaction ID provided in the report request.",
  refMsgId: "Reference Message ID corresponding to the report request.",
  msgId:
    "The unique Message ID generated by the system for the report processing.",
  buyerAppId: "The ID of the Buyer Application involved.",
  sellerAppId: "The ID of the Seller Application involved.",
  settlementId: "The Settlement ID associated with the report data.",
  txnId: "Transaction ID referenced in the report.",
  createdAt: "The date and time the report record was created.",
};

const Reports = () => {
  const today = new Date().toISOString().slice(0, 10);
  const {
    reports,
    loading,
    totalOrderCount,
    filters,
    setFilters,
    handleFilterChange,
    clearFilters,
    sortConfig,
    handleSort,
    paginatedReports,
    totalPages,
    currentPage,
    startItem,
    endItem,
    goToPage,
    itemsPerPage,
    handleItemsPerPageChange,
    filterOptions,
    selectedOption,
    setSelectedOption,
    searchValue,
    setSearchValue,
    handleDropdownChange,
    handleSearchChange,
    searchOptions,
    itemsPerPageOptions,
  } = useReports();

  // const canViewReports = usePermission("REPORT_LISTING");

  return (
    <>
      {/* {!canViewReports ? ( */}
      <div className="m-4">
        <p className="text-muted small mb-3">
          This page provides a consolidated view of generated reports. Use the
          filters below to search for specific reports.
        </p>

        {/* --- Filters (Styled like Orders) --- */}
        <div className="filters-wrapper bg-white px-3 py-2 rounded shadow-sm">
          <div className="row gx-4 gy-3 align-items-end flex-wrap">
            {/* Search Section - Styled like Generic Search in Orders */}
            <div className="col-auto">
              <label className="form-label mb-1">Search By</label>
              <div className="input-group" style={{ minWidth: "400px" }}>
                <select
                  className="form-select"
                  value={selectedOption}
                  onChange={handleDropdownChange}
                  style={{ maxWidth: "150px" }}
                >
                  {searchOptions?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="form-control"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder={`Enter ${selectedOption
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}`}
                  style={{
                    fontSize: "12px",
                    padding: "none",
                  }}
                />
              </div>
            </div>

            {/* Buyer App */}
            <div className="col-auto">
              <label className="form-label mb-1">Buyer App</label>
              <select
                className="form-select"
                value={filters.buyer_app_id || ""}
                onChange={(e) =>
                  handleFilterChange("buyer_app_id", e.target.value)
                }
                style={{ minWidth: "150px" }}
              >
                <option value="">All</option>
                {filterOptions?.buyerAppIds?.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Created From (Date Range start) - Harmonized styling */}
            <div className="col-auto">
              <label className="form-label mb-1">Created From</label>
              <input
                type="date"
                className="form-control"
                value={filters.settlement_created_at_from}
                onChange={(e) =>
                  handleFilterChange(
                    "settlement_created_at_from",
                    e.target.value
                  )
                }
                max={today}
                style={{ fontSize: "12px", padding: "none" }}
              />
            </div>

            {/* Created To (Date Range end) - Harmonized styling */}
            <div className="col-auto">
              <label className="form-label mb-1">Created To</label>
              <input
                type="date"
                className="form-control"
                value={filters.settlement_created_at_to}
                onChange={(e) =>
                  handleFilterChange("settlement_created_at_to", e.target.value)
                }
                max={today}
                min={filters.settlement_created_at_from || ""}
                style={{ fontSize: "12px", padding: "none" }}
              />
            </div>

            {/* Clear Filters Button - Styled like Orders Clear Filters button */}
            <div className="col-auto d-flex">
              <button
                type="button"
                className="btn btn-primary no-hover-btn"
                style={{ padding: "5px 8px" }}
                onClick={clearFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Reports table */}
        {loading ? (
          <AppLoader />
        ) : paginatedReports && paginatedReports.length > 0 ? (
          <>
            <div
              className="table-responsive mt-4"
              style={{ overflowX: "auto" }}
            >
              <table className="table table-bordered table-hover align-middle text-center">
                <thead className="table-light text-center">
                  <tr>
                    {/* 1. Reference Txn. ID */}
                    <th>
                      Reference Txn. ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reportColumnInfo.refTxnId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 2. Reference Msg ID */}
                    <th>
                      Reference Msg ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reportColumnInfo.refMsgId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 3. Msg ID */}
                    <th>
                      Msg ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reportColumnInfo.msgId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 4. Buyer App ID */}
                    <th>
                      Buyer App ID
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{reportColumnInfo.buyerAppId}</Tooltip>
                        }
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 5. Seller App ID */}
                    <th>
                      Seller App ID
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{reportColumnInfo.sellerAppId}</Tooltip>
                        }
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 6. Settlement ID */}
                    <th>
                      Settlement ID
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{reportColumnInfo.settlementId}</Tooltip>
                        }
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 7. Txn. ID */}
                    <th>
                      Txn. ID
                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>{reportColumnInfo.txnId}</Tooltip>}
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>

                    {/* 8. Created At - Sorting enabled */}
                    <th
                      onClick={() => handleSort("createdAt")}
                      role="button"
                      className="cursor-pointer"
                    >
                      Created At &nbsp;
                      {sortConfig.key === "createdAt" ? (
                        sortConfig.direction === "asc" ? (
                          <FaSortUp size={14} />
                        ) : (
                          <FaSortDown size={14} />
                        )
                      ) : (
                        <FaSort size={14} />
                      )}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>{reportColumnInfo.createdAt}</Tooltip>
                        }
                      >
                        <span style={{ marginLeft: "4px", cursor: "help" }}>
                          <Info size={14} className="text-muted" />
                        </span>
                      </OverlayTrigger>
                    </th>
                  </tr>
                </thead>
                <tbody className="text-center">
                  {paginatedReports.map((report, index) => (
                      <tr key={`${report.ref_txn_id}-${index}`}>
                        {/* 1. Reference Transaction ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {report.ref_txn_id}
                          </span>
                          <CopyIcon textToCopy={report.ref_txn_id} />
                        </td>

                        {/* 2. Reference Message ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {report.ref_message_id}
                          </span>
                          <CopyIcon textToCopy={report.ref_message_id} />
                        </td>

                        {/* 3. Message ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">{report.msg_id}</span>
                          <CopyIcon textToCopy={report.msg_id} />
                        </td>

                        {/* 4. Buyer App ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {report.buyer_app_id}
                          </span>
                          <CopyIcon textToCopy={report.buyer_app_id} />
                        </td>

                        {/* 5. Seller App ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {report.seller_app_id}
                          </span>
                          <CopyIcon textToCopy={report.seller_app_id} />
                        </td>

                        {/* 6. Settlement ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">
                            {report.settlement_id}
                          </span>
                          <CopyIcon textToCopy={report.settlement_id} />
                        </td>

                        {/* 7. Transaction ID - Removed minWidth */}
                        <td className="copy-cell">
                          <span className="truncate-cell">{report.txn_id}</span>
                          <CopyIcon textToCopy={report.txn_id} />
                        </td>

                        {/* 8. Created At - Removed minWidth */}
                        <td>{formatDateTime(report.createdAt)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#9ca3af",
              fontSize: "14px",
              marginTop: "16px",
            }}
          >
            No Report Found
          </div>
        )}

        {/* Pagination */}
        {totalOrderCount > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            startItem={startItem}
            endItem={endItem}
            totalItems={totalOrderCount}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            onPageChange={goToPage}
            itemsPerPageOptions={itemsPerPageOptions}
          />
        )}
      </div>
      {/* // ) : (
      //   <UnAuthorized />
      // )} */}
    </>
  );
};

export default Reports;
